import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import { Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/admin/vault-collections`: pair each Vault collection with its Hub project
 * in one go, as its main or prod collection. The pairing by name is a suggestion.
 */

export const Route = createFileRoute("/_app/admin/vault-collections")({
  component: VaultCollectionsPage,
});

type Collection = {
  id: string;
  name: string;
  kind: "main" | "prod";
  boundProjectId: string | null;
  suggestedProjectId: string | null;
  team: boolean;
};
type Project = { id: string; name: string };

function VaultCollectionsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [collections, setCollections] = useState<Collection[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ collections: Collection[]; projects: Project[] }>(
        "/api/admin/vault-collections",
        { timeoutMs: 60_000 },
      );
      setCollections(res.collections);
      setProjects(res.projects);
      setPicked(Object.fromEntries(res.collections.map((c) => [c.id, c.suggestedProjectId ?? ""])));
    } catch (err) {
      setMessage(
        err instanceof ApiError && err.status === 503
          ? "Vault access isn't configured (VW_ACCESS_*)."
          : `Couldn't read the collections from Vault: ${apiErrorMessage(err)}`,
      );
    }
  }, []);

  useEffect(() => {
    if (user.globalRole === "admin") void load();
  }, [user.globalRole, load]);

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  async function setTeam(c: Collection, team: boolean) {
    setBusy(true);
    setMessage(null);
    try {
      await api.put(`/api/admin/vault-collections/${c.id}/team`, { team }, { timeoutMs: 60_000 });
      setMessage(
        team ? `${c.name} is now for the whole team.` : `${c.name} is no longer the team's.`,
      );
      await load();
    } catch (err) {
      setMessage(`Couldn't change ${c.name}: ${apiErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  const slots = (collections ?? [])
    .filter((c) => picked[c.id])
    .map((c) => `${picked[c.id]}:${c.kind}`);
  const clash = slots.length !== new Set(slots).size;
  const clashing = new Set(slots.filter((slot, i) => slots.indexOf(slot) !== i));
  const clashes = (c: Collection) =>
    Boolean(picked[c.id]) && clashing.has(`${picked[c.id]}:${c.kind}`);

  async function bind() {
    if (!collections) return;
    setBusy(true);
    setMessage(null);
    try {
      const rows = collections
        .filter((c) => picked[c.id])
        .map((c) => ({ projectId: picked[c.id], collectionId: c.id }));
      const res = await api.post<{ bound: number }>(
        "/api/admin/vault-collections/bind",
        { rows },
        { timeoutMs: 60_000 },
      );
      setMessage(`Bound ${res.bound} collection(s).`);
      await load();
    } catch (err) {
      setMessage(`Couldn't bind them: ${apiErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Vault collections</h1>
          <p className="text-sm text-muted-foreground">
            Every collection of the organisation, with the project it looks like it belongs to. One
            with a "prod" segment becomes the project's prod collection (managers only), any other
            its main one. A collection for everyone (like Services) goes to every person on the
            team, not customers or agents. Leave a collection unpaired and Hub never touches it.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => void bind()}
          disabled={busy || slots.length === 0 || clash}
          data-testid="bind-collections"
        >
          Bind {slots.length}
        </Button>
      </div>
      {message && <p className="text-sm">{message}</p>}
      {clash && (
        <p className="text-sm text-destructive">
          A project is picked twice for the same kind of collection: see the highlighted rows.
        </p>
      )}
      {!collections && !message && <p className="text-sm text-muted-foreground">Loading…</p>}
      {collections && (
        <Card>
          <CardContent className="divide-y pt-2">
            {collections.map((c) => (
              <div
                key={c.id}
                className={`flex items-center justify-between gap-3 py-2 ${clashes(c) ? "bg-destructive/10" : ""}`}
                data-testid="vault-collection"
                data-clash={clashes(c) ? "true" : undefined}
              >
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant={c.kind === "prod" ? "destructive" : "secondary"}>{c.kind}</Badge>
                  <span className="font-medium">{c.name}</span>
                  {c.boundProjectId && <Badge variant="outline">bound</Badge>}
                  {c.team && <Badge>everyone</Badge>}
                  {clashes(c) && (
                    <span className="text-xs text-destructive">
                      same project and kind as another row
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!c.boundProjectId && c.kind === "main" && (
                    <Button
                      type="button"
                      size="sm"
                      variant={c.team ? "secondary" : "outline"}
                      disabled={busy}
                      onClick={() => void setTeam(c, !c.team)}
                      aria-label={`${c.team ? "Not for everyone" : "For everyone"}: ${c.name}`}
                    >
                      {c.team ? "Not for everyone" : "For everyone"}
                    </Button>
                  )}
                  {!c.team && (
                    <Select
                      size="sm"
                      aria-label={`Project for ${c.name}`}
                      value={picked[c.id] ?? ""}
                      onChange={(v) => setPicked((prev) => ({ ...prev, [c.id]: v }))}
                      options={[
                        { value: "", label: "— not a project —" },
                        ...projects.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                    />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
